import { useRef, useState } from "react";
import { DatabaseBackup, Download, Upload, Loader2, CloudDownload } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, upload } from "@/lib/api";

const Report = ({ rep }) => (
  <div className="mt-3 text-xs text-slate-600 grid grid-cols-2 sm:grid-cols-3 gap-1" data-testid="data-import-report">
    {Object.entries(rep).filter(([, v]) => v.added || v.skipped).map(([k, v]) => <span key={k}><b>{k.replace(/_/g, " ")}</b>: +{v.added}{v.skipped ? ` (${v.skipped} already there)` : ""}</span>)}
  </div>
);

const PullForm = ({ busy, onStart }) => {
  const [f, setF] = useState({ site_url: "", email: "", password: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const input = "border border-slate-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-500";
  return (
    <form onSubmit={(e) => { e.preventDefault(); onStart(f); }} className="bg-blue-50/60 border border-blue-100 rounded-lg p-4 mb-4 space-y-2" data-testid="data-pull-form">
      <div className="font-semibold text-sm text-slate-900 flex items-center gap-1.5"><CloudDownload className="w-4 h-4 text-blue-600" />One-click copy from another site</div>
      <p className="text-xs text-slate-500">Use this on your <b>live</b> site. Enter your preview site's address and its admin login. Everything is copied over directly and duplicates are skipped.</p>
      <input className={input} placeholder="https://your-app.preview.emergentagent.com" value={f.site_url} onChange={set("site_url")} required data-testid="data-pull-url" />
      <div className="grid sm:grid-cols-2 gap-2">
        <input className={input} type="email" placeholder="Admin email on that site" value={f.email} onChange={set("email")} required data-testid="data-pull-email" />
        <input className={input} type="password" placeholder="Admin password on that site" value={f.password} onChange={set("password")} required data-testid="data-pull-password" />
      </div>
      <button disabled={!!busy} className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5" data-testid="data-pull-btn">
        {busy === "pull" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudDownload className="w-4 h-4" />}{busy === "pull" ? "Copying…" : "Copy everything now"}
      </button>
    </form>
  );
};

export const DataTransferCard = () => {
  const ref = useRef(null);
  const [busy, setBusy] = useState("");
  const [job, setJob] = useState(null);
  const poll = async (id) => {
    for (;;) {
      await new Promise(r => setTimeout(r, 1500));
      try {
        const { data } = await api.get(`/api/admin/data/jobs/${id}`);
        setJob(data);
        if (data.status !== "running") { (data.status === "done" ? toast.success : toast.error)(data.message); return; }
      } catch { /* server busy, keep polling */ }
    }
  };
  const run = async (kind, fn) => {
    setBusy(kind); setJob(null);
    try { const { data } = await fn(); setJob({ status: "running", step: 0, steps: 1, report: {} }); await poll(data.job_id); }
    catch (e) { toast.error(errMsg(e, "Import failed")); }
    setBusy("");
  };
  const exp = async () => {
    setBusy("export");
    try {
      const r = await api.get("/api/admin/data/export", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "shopadvisor-backup.json.gz";
      a.click(); URL.revokeObjectURL(a.href);
      toast.success("Backup downloaded");
    } catch (e) { toast.error(errMsg(e, "Export failed")); }
    setBusy("");
  };
  const imp = (f) => {
    ref.current.value = "";
    if (!f || !window.confirm(`Import "${f.name}" into this site? Records already here are kept and never overwritten.`)) return;
    const fd = new FormData(); fd.append("file", f);
    run("import", () => upload("/api/admin/data/import", fd));
  };
  const pct = job ? Math.round(((job.step || 0) / (job.steps || 1)) * 100) : 0;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm mt-4 max-w-3xl" data-testid="data-transfer-card">
      <div className="flex items-center gap-2 mb-1"><DatabaseBackup className="w-5 h-5 text-blue-600" /><h3 className="font-bold text-slate-900">Move data between sites</h3></div>
      <p className="text-sm text-slate-500 mb-4">Brings over leads, customers, reps, referral agents, plans, audits, billing, jobs, quotes, orders, gift cards, promos, scripts and payroll.</p>
      <PullForm busy={busy} onStart={(f) => run("pull", () => api.post("/api/admin/data/pull", f))} />
      <div className="text-xs text-slate-500 mb-2">Or use a backup file: download it on one site, import it on the other.</div>
      <div className="flex flex-wrap gap-2">
        <button onClick={exp} disabled={!!busy} className="border border-slate-300 text-slate-700 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5" data-testid="data-export-btn">{busy === "export" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Download backup</button>
        <button onClick={() => ref.current.click()} disabled={!!busy} className="border border-slate-300 text-slate-700 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5" data-testid="data-import-btn">{busy === "import" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{busy === "import" ? "Importing…" : "Import backup"}</button>
        <input ref={ref} type="file" accept=".gz,.json" className="hidden" onChange={e => imp(e.target.files[0])} data-testid="data-import-input" />
      </div>
      {job && (
        <div className="mt-4" data-testid="data-import-progress">
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-blue-600 transition-[width] duration-500" style={{ width: `${job.status === "done" ? 100 : pct}%` }} /></div>
          <div className={`text-sm mt-2 ${job.status === "failed" ? "text-red-600" : "text-slate-700"}`} data-testid="data-import-message">{job.status === "running" ? `Copying… ${pct}%` : job.message}</div>
          {job.report && <Report rep={job.report} />}
        </div>
      )}
    </div>
  );
};
